import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { DisplayService } from '../service/display.service';
import { FactorselectionService } from '../service/factorselection.service';
import { LdapService } from '../service/ldap.service';
import { Subscription } from "rxjs";

const CHANGE_PWD_ERR_CODE = 1003033;
const CHANGE_PWD_HTTP_CODE = 428;

@Component({
  selector: 'app-ldap',
  templateUrl: './ldap.component.html'
})
export class LdapComponent implements OnInit, OnDestroy {
  passwordEnabled = false;
  errorMsg: String;
  errorDiv: boolean = false;
  userName = null;
  passwordForm: UntypedFormGroup;
  factorSelection: boolean = false;
  inlineMessage: string;
  ready: boolean = true;
  pageSubs: Subscription[] = new Array<Subscription>();
  public currentPwdResetExtLink: string;
  private readonly DEFAULT_PWD_RESET_LINK: string = "https://nexen.bnymellon.com/self-service/nxn/fps";
  private readonly PWD_RESET_EXT_LINK_REGION_MAP: Map<string, string> = new Map([
    ['localhost', 'https://nexen.test.bnymellon.com/self-service/nxn/fps'],
    ['.dev.', 'https://nexen.test.bnymellon.com/self-service/nxn/fps'],
    ['.test.', 'https://nexen.test.bnymellon.com/self-service/nxn/fps'],
    ['.qa.', 'https://nexen.qa.bnymellon.com/self-service/nxn/fps']
  ]);

  constructor(private readonly factorselectionService: FactorselectionService,
              private readonly formBuilder: UntypedFormBuilder,
              private readonly router: Router,
              private readonly ldapService: LdapService,
              private readonly displayService: DisplayService,
              @Inject('Window') private readonly window: Window) {
    this.displayService.disableBrowserBackButton();
  }

  ngOnInit() {
    if (this.displayService.userName == null) {
      this.router.navigate(["../"]);
    }
    this.inlineMessage = (this.displayService.inlineMessage || "").replace(/FIDO/g, "biometrics").replace(/SECURITYKEY/g, 'security key').replace(/PASSKEY/g, 'passkey');
    this.passwordForm = this.formBuilder.group({
      password: ['', [Validators.required, Validators.minLength(1)]]
    });
    this.passwordEnabled = this.displayService.passwordEnabled;
    this.factorSelection = this.displayService.factorSelection;
    this.displayService.focusNthField();
    this.getPasswordResetLinkForCurrentHost();
  }

  onPasswordSubmit() {
    this.errorDiv = false;
    this.ready = false;

    this.pageSubs = [this.ldapService.onPasswordLogin(this.passwordForm.value.password)
      .subscribe({
        next: (data) => {
          this.displayService.routeActions(data);
        },
        error: (err) => {
          if (this.checkChangePasswordError(err)) {
            this.displayService.routeActions(err.error);
          } else {
            this.errorDiv = this.displayService.errorDiv;
            this.errorMsg = this.displayService.errorMsg;
            this.ready = true;
          }
        }
      })
    ];
  }

  /**
   * Check if the error matches Broadcom's change password response and error code.
   *
   * Broadcom will throw error even when password succeeds, if the password must change. The change password action
   * will be invoked after the user completes all factors for the policy.
   *
   * Official documentation: https://techdocs.broadcom.com/us/en/symantec-security-software/identity-security/vip-authentication-hub/2-2/Using/Authentication-services/factor-services/Password-Authentication.html
   * @param err
   */
  checkChangePasswordError(err: any) {
    return (err?.status == CHANGE_PWD_HTTP_CODE && err?.error?.data?.errorCode == CHANGE_PWD_ERR_CODE);
  }


  factorSelect() {
    this.errorDiv = false;
    this.ready = false;
    this.pageSubs.push(
      this.factorselectionService.chooseAnother()
    );
  }

  ngOnDestroy() {
    this.pageSubs.forEach((sub) => {
      sub?.unsubscribe();
    });
  }

  /**
   * Searched the current hostname, for any of the configured region keys in the map.
   * If the hostname contains the value, then the corresponding URL is assigned to this.currentPwdResetExtLink
   * @returns void
   * @sets this.currentPwdResetExtLink
   */
  public getPasswordResetLinkForCurrentHost(): void {
    const currentHostName: string = this.window.location.hostname;
    const mappedRegionSegments = [...this.PWD_RESET_EXT_LINK_REGION_MAP];
    let matchedLink: string = null;

    mappedRegionSegments.some(regionSegment => {     
      matchedLink = currentHostName.includes(regionSegment[0]) ? this.PWD_RESET_EXT_LINK_REGION_MAP.get(regionSegment[0]) :  null;
      if(matchedLink) {
        this.currentPwdResetExtLink = matchedLink;
        return true;
      }
      else {
        this.currentPwdResetExtLink = this.DEFAULT_PWD_RESET_LINK;
      } 
    });
  }
}